// src/components/AttendanceDataTable.jsx
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import MUIDataTable from "mui-datatables";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import DynamicModal from "./DynamicModal";
import EditAttendanceModal from "./EditAttendanceModal";
import {
  getAttendance,
  deleteAttendance,
  updateTask,
  clearAttendanceState,
} from "../redux/slices/attendanceSlice";
import DateFilter from "./DateFilter";
import useDateFilter from "./useDateFilter";
import CustomerGroupFilter from "./CustomerGroupFilter";

const AttendanceDataTable = forwardRef((props, ref) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { attendanceList, isLoading, error, isDeleting } = useSelector(
    (state) => state.attendance,
  );

  // Use date filter hook
  const { dateFilter, updateFilter, getDateRange } = useDateFilter();

  // Add state for customer group filter
  const [customerGroup, setCustomerGroup] = useState("");

  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
  const [selectedFacility, setSelectedFacility] = useState("All");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const buttonRefs = useRef([]);

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  // Create a ref for the fetch function to be used by the parent
  const fetchDataRef = useRef();

  // Show toast function
  const showToast = (message, type = "info") => {
    setToast({
      show: true,
      message,
      type,
    });
    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  // Fetch attendance function - can be called from parent via ref
  const fetchAttendance = () => {
    const dateRange = getDateRange();
    dispatch(
      getAttendance({
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        customer_group_name: customerGroup || undefined,
      }),
    );
  };

  // Store fetch function in ref
  fetchDataRef.current = fetchAttendance;

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    refreshData: () => {
      fetchAttendance();
    },
    getFilterParams: () => {
      const dateRange = getDateRange();
      return {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        customer_group_name: customerGroup || undefined,
      };
    },
    getCurrentFilters: () => {
      return {
        dateFilter,
        customerGroup,
        selectedFacility,
      };
    },
  }));

  // Fetch attendance with date filters and customer group filter
  useEffect(() => {
    fetchAttendance();
  }, [
    dispatch,
    dateFilter.selectedYear,
    dateFilter.viewType,
    dateFilter.selectedQuarter,
    dateFilter.selectedMonth,
    customerGroup,
  ]);

  // Handle customer group filter change
  const handleGroupChange = (group) => {
    setCustomerGroup(group);
  };

  const handleFacilityFilter = (facility) => {
    setSelectedFacility(facility);
  };

  const handleDropdownToggle = (index, e) => {
    e.stopPropagation();

    if (buttonRefs.current[index]) {
      const rect = buttonRefs.current[index].getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 120,
      });
    }

    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const handleViewDetails = (rowData) => {
    setSelectedAttendance(rowData);
    setShowViewModal(true);
    setDropdownOpen(null);
  };

  const handleEdit = (rowData) => {
    setSelectedAttendance(rowData);
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedAttendance(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedAttendance(null);
  };

  const handleDelete = (rowData) => {
    if (
      window.confirm(
        `Are you sure you want to delete attendance record for "${rowData.title}"?`,
      )
    ) {
      dispatch(deleteAttendance(rowData.id)).then((result) => {
        if (result.payload?.success) {
          showToast("Attendance record deleted successfully!", "success");
          fetchAttendance();
        }
      });
    }
    setDropdownOpen(null);
  };

  const handleSaveTask = (taskData) => {
    setIsSaving(true);

    // Parse the datetime-local string (format: YYYY-MM-DDTHH:MM)
    const parseDateTimeLocal = (dateTimeStr) => {
      const [datePart, timePart] = dateTimeStr.split("T");
      const [year, month, day] = datePart.split("-");
      const [hours, minutes] = timePart.split(":");

      return {
        year,
        month,
        day,
        hours,
        minutes: minutes || "00",
      };
    };

    const startParts = parseDateTimeLocal(taskData.startDateTime);
    const endParts = parseDateTimeLocal(taskData.endDateTime);

    const updateData = {
      id: selectedAttendance.id,
      title: taskData.title,
      description: taskData.description || "",
      start_time: `${startParts.year}-${startParts.month}-${startParts.day} ${startParts.hours}:${startParts.minutes}:00`,
      end_time: `${endParts.year}-${endParts.month}-${endParts.day} ${endParts.hours}:${endParts.minutes}:00`,
    };

    dispatch(updateTask(updateData))
      .then((action) => {
        setIsSaving(false);

        if (action.payload?.success) {
          showToast("Attendance record updated successfully!", "success");
          handleCloseEditModal();
          handleCloseViewModal();
          fetchAttendance();
        } else {
          showToast(
            action.payload?.message || "Failed to update attendance record",
            "error",
          );
        }
      })
      .catch((error) => {
        setIsSaving(false);
        showToast("An error occurred while saving", "error");
        console.error("Save error:", error);
      });
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setDropdownOpen(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const calculateHoursWorked = (startTime, endTime) => {
    if (!startTime || !endTime) return "N/A";

    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const diffInMs = end - start;
      const diffInHours = diffInMs / (1000 * 60 * 60);
      return `${diffInHours.toFixed(1)}`;
    } catch (error) {
      return "N/A";
    }
  };

  const transformAttendanceData = (apiData) => {
    return apiData.map((task) => {
      const hoursWorked = calculateHoursWorked(task.start_time, task.end_time);

      let month = "N/A";
      if (task.start_time) {
        try {
          const startDate = new Date(task.start_time);
          month = startDate.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
          });
        } catch (error) {
          month = "N/A";
        }
      }

      return {
        id: task.id || "N/A",
        title: task.title || "No Title",
        description: task.description || "No Description",
        startTime: task.start_time
          ? new Date(task.start_time).toLocaleTimeString("en-US", {
              hour12: true,
            })
          : "N/A",
        endTime: task.end_time
          ? new Date(task.end_time).toLocaleTimeString("en-US", {
              hour12: true,
            })
          : "N/A",
        month: month,
        customergroupname: task.customer_group_name || "N/A",
        employeeName: task.user_name || `User ${task.user_id || "N/A"}`,
        employeeId: task.user_id || "N/A",
        employeeEmail: task.user_email || "N/A",
        facility: task.facility_name || "Unknown Facility",
        hoursWorked: hoursWorked,
        originalData: task,
      };
    });
  };

  const attendanceData = isLoading
    ? []
    : transformAttendanceData(attendanceList);

  const filteredData =
    selectedFacility === "All"
      ? attendanceData
      : attendanceData.filter((task) => {
          if (selectedFacility === "KFC Facility")
            return (
              task.facility === "KFC" ||
              task.facility.toLowerCase().includes("kfc")
            );
          if (selectedFacility === "Starbucks Facility")
            return (
              task.facility === "Starbucks" ||
              task.facility.toLowerCase().includes("starbucks")
            );
          if (selectedFacility === "Burger King Facility")
            return (
              task.facility === "Burger King" ||
              task.facility.toLowerCase().includes("burger king")
            );
          return true;
        });

  const columns = [
    {
      name: "employeeName",
      label: "Employee Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const rowData = filteredData[tableMeta.rowIndex];
          return (
            <div>
              <div style={{ fontWeight: "500", color: "#1A1A1A" }}>{value}</div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {rowData.employeeEmail}
              </div>
            </div>
          );
        },
      },
    },
    {
      name: "customergroupname",
      label: "Group Name",
    },
    {
      name: "employeeId",
      label: "Employee ID",
      options: {
        customBodyRender: (value) => {
          return (
            <div style={{ color: "#666", fontWeight: "400" }}>ID: {value}</div>
          );
        },
      },
    },
    {
      name: "facility",
      label: "Facility",
      options: {
        customBodyRender: (value) => {
          return (
            <div
              style={{
                padding: "4px 8px",
                backgroundColor: "#E5F4FF",
                color: "#1976D2",
                borderRadius: "12px",
                fontSize: "0.85rem",
                fontWeight: 500,
                display: "inline-block",
              }}>
              {value}
            </div>
          );
        },
      },
    },
    {
      name: "startTime",
      label: "Check-In",
    },
    {
      name: "endTime",
      label: "Check-Out",
    },
    {
      name: "hoursWorked",
      label: "Hours Worked",
      options: {
        customBodyRender: (value) => {
          let color = "#666";
          let displayValue = value;

          if (value !== "N/A") {
            const hoursNum = parseFloat(value);
            if (!isNaN(hoursNum)) {
              if (hoursNum < 4) color = "#D32F2F";
              else if (hoursNum < 8) color = "#FF8104";
              else color = "#29BF5A";

              displayValue = `${hoursNum} hours`;
            }
          }

          return <div style={{ color, fontWeight: "500" }}>{displayValue}</div>;
        },
      },
    },
    {
      name: "month",
      label: "Month",
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        customBodyRenderLite: (dataIndex) => {
          return (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                ref={(el) => (buttonRefs.current[dataIndex] = el)}
                onClick={(e) => handleDropdownToggle(dataIndex, e)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                }}>
                <Icon icon="mdi:dots-horizontal" width="25" height="25" />
              </button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    searchPlaceholder: "Search by employee name, facility...",
    pagination: false,
    tableBodyHeight: "auto",
    textLabels: {
      body: {
        noMatch: isLoading
          ? "Loading..."
          : "No attendance records found for selected period",
      },
    },
  };

  // View Attendance Modal Component
  const ViewAttendanceModal = ({ attendance, onClose, onEdit, onDelete }) => {
    if (!attendance) return null;

    const formatDateTime = (dateTimeStr) => {
      if (!dateTimeStr || dateTimeStr === "N/A") return "N/A";
      try {
        const date = new Date(dateTimeStr);
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return dateTimeStr;
      }
    };

    return (
      <div>
        <div
          style={{
            background: "#F7F8FA",
            padding: "20px",
            borderRadius: "16px",
            marginBottom: "20px",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}>
          <h3
            style={{
              margin: "0 0 10px 0",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              color: "#8B2885",
            }}>
            {attendance.title}
          </h3>

          <div style={{ marginBottom: "15px" }}>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Employee:</strong> {attendance.employeeName}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Email:</strong> {attendance.employeeEmail}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Facility:</strong> {attendance.facility}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Group:</strong> {attendance.customergroupname}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Check-In:</strong>{" "}
              {formatDateTime(attendance.originalData?.start_time)}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Check-Out:</strong>{" "}
              {formatDateTime(attendance.originalData?.end_time)}
            </p>
            <p
              style={{
                fontSize: "14px",
                color: "#666",
                margin: "5px 0",
              }}>
              <strong>Hours Worked:</strong>{" "}
              {attendance.hoursWorked !== "N/A"
                ? `${attendance.hoursWorked} hours`
                : "N/A"}
            </p>
          </div>

          <div style={{ marginTop: "20px" }}>
            <strong style={{ display: "block", marginBottom: "8px" }}>
              Description:
            </strong>
            <p
              style={{
                margin: "0",
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                wordBreak: "break-word",
                maxWidth: "100%",
                fontSize: "14px",
                lineHeight: "1.5",
                background: "white",
                padding: "15px",
                borderRadius: "12px",
              }}>
              {attendance.description || "No description provided."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Date Filter and Customer Group Filter */}
      <div className="mb-4 pb-2 border-bottom">
        <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
          <div style={{ minWidth: "300px" }}>
            <DateFilter
              {...dateFilter}
              onFilterChange={updateFilter}
              size="sm"
            />
          </div>

          <div className="flex-grow-1" style={{ minWidth: "250px" }}>
            <CustomerGroupFilter
              selectedGroup={customerGroup}
              onGroupChange={handleGroupChange}
              size="sm"
            />
          </div>
        </div>

        {/* Active filter badges */}
        <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
          <span className="badge bg-light text-dark p-2">
            <i className="fas fa-calendar-alt me-1"></i>
            {getDateRange().label}
          </span>

          {customerGroup && (
            <span className="badge bg-info text-white p-2">
              <i className="fas fa-tag me-1"></i>
              Group: {customerGroup}
              <button
                onClick={() => {
                  setCustomerGroup("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  marginLeft: "8px",
                  padding: "0 4px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}>
                ×
              </button>
            </span>
          )}

          {!isLoading && attendanceList.length > 0 && (
            <span className="badge bg-success p-2">
              <i className="fas fa-clock me-1"></i>
              {attendanceList.length} Records
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "10px" }}>Loading attendance data...</p>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#D32F2F" }}>
          <Icon icon="material-symbols:error-outline" width="48" height="48" />
          <p style={{ marginTop: "10px" }}>Error: {error}</p>
          <button
            onClick={() => {
              fetchAttendance();
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#8B2885",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
            }}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {attendanceData.length === 0 ? (
            <div
              style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <Icon icon="mdi:calendar-clock" width="48" height="48" />
              <p style={{ marginTop: "10px" }}>
                No attendance records found for {getDateRange().label}
                {customerGroup && ` for group "${customerGroup}"`}.
              </p>
              <p style={{ fontSize: "14px", marginTop: "8px", color: "#999" }}>
                Try selecting a different date range or customer group
              </p>
            </div>
          ) : (
            <div className="basic-data-table">
              <MUIDataTable
                title=""
                data={filteredData}
                columns={columns}
                options={options}
                className="overflow-hidden packageTable"
              />
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      <DynamicModal
        show={showViewModal}
        handleClose={handleCloseViewModal}
        title="Attendance Details"
        modalWidth="500px"
        content={
          <ViewAttendanceModal
            attendance={selectedAttendance}
            onClose={handleCloseViewModal}
            onEdit={(attendance) => {
              setSelectedAttendance(attendance);
              setShowEditModal(true);
            }}
            onDelete={(attendance) => {
              handleDelete(attendance);
            }}
          />
        }
      />

      {/* Edit Modal */}
      <DynamicModal
        show={showEditModal}
        handleClose={handleCloseEditModal}
        title={`Edit Attendance - ${selectedAttendance?.title || ""}`}
        modalWidth="500px"
        content={
          <EditAttendanceModal
            attendanceData={selectedAttendance}
            isEditMode={true}
            onClose={handleCloseEditModal}
            onSave={handleSaveTask}
            isSaving={isSaving}
            onSuccess={() => {
              // Refresh the data
              const dateRange = getDateRange();
              dispatch(
                getAttendance({
                  from_date: dateRange.from_date,
                  to_date: dateRange.to_date,
                  customer_group_name: customerGroup || undefined,
                }),
              );
              handleCloseEditModal();
            }}
          />
        }
      />

      {dropdownOpen !== null &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: buttonPosition.top,
              left: buttonPosition.left,
              backgroundColor: "white",
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              minWidth: "140px",
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleViewDetails(filteredData[dropdownOpen])}>
              <Icon
                icon="mdi:eye-outline"
                width="18"
                height="18"
                color="#2196F3"
              />
              <span>View Details</span>
            </div>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#333",
                borderTop: "1px solid #F0F0F0",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleEdit(filteredData[dropdownOpen])}>
              <Icon icon="line-md:edit" width="18" height="18" color="#666" />
              <span>Edit</span>
            </div>
            <div
              style={{
                padding: "10px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "14px",
                color: "#D32F2F",
                borderTop: "1px solid #F0F0F0",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F5F5F5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
              onClick={() => handleDelete(filteredData[dropdownOpen])}>
              <Icon
                icon="material-symbols:delete-outline"
                width="18"
                height="18"
                color="#D32F2F"
              />
              <span>{isDeleting ? "Deleting..." : "Delete"}</span>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

export default AttendanceDataTable;
